/**
 * Générateur — Utiliser un coefficient de proportionnalité dans des contextes concrets
 * (prix unitaire, vitesse moyenne, échelle, etc.).
 * Attendu ID : 984 | Niveau : 5ème | Domaine : proportionnalite
 *
 * ═══════════════════════════════════════════════════════════════════
 * PLAN DE CARTES (version conforme à la spécification 984.json, 2026-09-21)
 * ═══════════════════════════════════════════════════════════════════
 *
 * Libellé officiel (spec.attendu) : « Utiliser un coefficient de proportionnalité dans des
 * contextes concrets (prix unitaire, vitesse moyenne, échelle, etc.). »
 *
 * Catégorie « prix » (prix unitaire et change monétaire ; coefficient entier ou décimal
 * supérieur à 1 ; niveaux 1 et 2)
 *   - prix_qcm         QCM : prix total, ou prix d'une unité à retrouver
 *   - prix_texte       Saisie : prix en euros, unité exigée
 *   - prix_relier      Relier : quatre quantités ↔ quatre prix, même prix unitaire
 *   - prix_tableau_vf  V/F : quatre affirmations sur un prix unitaire
 *
 * Catégorie « vitesse » (vitesse moyenne ; coefficient entier ; niveaux 1, 2 et 4)
 *   - vitesse_qcm        QCM : distance parcourue, ou vitesse moyenne à retrouver
 *   - vitesse_texte      Saisie : distance (km), durée (min) ou vitesse (coefficient)
 *   - vitesse_tableau_vf V/F : quatre affirmations sur un trajet à allure constante
 *   - vitesse_texte_trou Trous (drag) : tableau durée/distance, grandeurs et unités nommées
 *
 * Catégorie « echelle » (échelle d'un plan et consommation à l'unité ; coefficient inférieur
 * à 1, fractionnaire ou décimal ; niveaux 3 et 4)
 *   - echelle_qcm                   QCM : longueur réelle à partir d'une échelle
 *   - echelle_texte                 Saisie : longueur réelle ou longueur sur le plan, unité exigée
 *   - echelle_calcul_echelle_texte  Saisie : l'échelle elle-même, en fraction
 *   - echelle_relier                Relier : échelle ↔ « 1 cm représente … »
 *   - echelle_tableau_vf            V/F : quatre affirmations sur un plan à l'échelle
 *   - echelle_texte_trou            Trous (drag) : tableau plan/réalité
 *   - consommation_texte            Saisie (AJOUTÉE) : consommation à l'unité, coefficient < 1
 *
 * ── Spécification appliquée ──
 * Bornes :
 *   - contexte_de_la_situation : les trois contextes prescrits (prix unitaire, vitesse
 *     moyenne, échelle) et deux des trois que la spec ajoute (consommation par unité, change
 *     monétaire). Le rendement d'une recette n'est pas servi : voir « Non tranché ».
 *   - valeur_du_coefficient : [0,05 ; 150]. Prix unitaires de 0,40 € à 60 €, vitesses de 4 à
 *     130 km/h, échelles de 1/100 à 1/500 000, consommation de 0,05 à 0,12 L par kilomètre.
 *   - nature_du_coefficient : les quatre natures servies — entier (vitesse, prix rond),
 *     décimal supérieur à 1 (prix unitaire, taux de change), décimal inférieur à 1
 *     (consommation), fractionnaire (échelle).
 *   - sens_d_utilisation : appliquer (multiplier) et remonter (diviser), tirés à parité sur
 *     chaque carte qui porte les deux (point de vigilance 6).
 *   - valeur_de_la_grandeur_de_depart : [0,5 ; 500].
 *   - prix_unitaire : [0,40 ; 60], deux décimales.
 *   - vitesse_moyenne : [4, 130] entier ; jamais 60 aux niveaux 1 et 2 (exclusion 5).
 *   - duree_du_trajet : [6, 240] minutes entières ; jamais 60 aux niveaux 1 et 2.
 *     Le couple (vitesse, durée) donne toujours une distance à une décimale au plus.
 *   - echelle_d_un_plan : {100, 200, 500, 1 000, 2 000, 5 000, 25 000, 50 000, 100 000,
 *     500 000}.
 *   - unite_de_la_reponse : euro, kilomètre, mètre, centimètre, minute, heure, litre. L'unité
 *     fait partie de la réponse et la consigne l'annonce ; toutes les écritures justes de la
 *     même grandeur sont admises (liste finie, `contenu.reponses_acceptees`,
 *     `comparaison = 'expression'`). Seul un COEFFICIENT demandé explicitement (vitesse
 *     moyenne, échelle) se rend sans unité — `type_reponse` le prévoit.
 * Exclusions respectées : aucune conversion de km/h en m/s ni en m/min, aucune vitesse traitée
 *   comme grandeur quotient composée, aucun produit en croix, aucune quatrième proportionnelle,
 *   aucune situation sans contexte (toutes les grandeurs sont nommées et portent leur unité),
 *   aucun résultat à plus de deux décimales, aucune vitesse de 60 km/h ni durée de 60 minutes
 *   aux niveaux 1 et 2, aucun ratio ni rapport.
 *
 * Erreurs visées → mots-clés (codes des distracteurs et des cartes qui les provoquent) :
 *   1. multiplier quand il faut diviser, et l'inverse     → `operation_inversee`
 *   2. croire qu'un coefficient inférieur à 1 ne se multiplie pas
 *                                                          → `coefficient_inferieur_a_un_divise`
 *   3. mélanger deux unités dans le même calcul (minutes et heures, centimètres et kilomètres)
 *                                                          → `unites_melangees`
 *   4. rendre une durée en heures et minutes quand on demande des minutes, ou l'inverse
 *                                                          → `duree_en_h_min_au_lieu_de_minutes`
 *   5. confondre 1 h 25 min et 1,25 h                      → `heure_decimale_confondue`
 *   6. rendre un nombre sans unité, ou dans l'unité de l'énoncé plutôt que dans celle demandée
 *                                                          → `nombre_sans_unite`
 *   Compléments (familles de distracteurs supplémentaires, hors spec) :
 *      `virgule_decalee`, `coefficient_pris_pour_le_resultat`.
 *
 * Graduation → cartes :
 *   niveau 1 → prix_qcm, prix_texte, prix_relier, prix_tableau_vf (sens « appliquer »),
 *              vitesse_qcm, vitesse_texte (sens « appliquer »)
 *   niveau 2 → prix_qcm, prix_texte, prix_tableau_vf (sens « remonter »), vitesse_qcm,
 *              vitesse_texte, vitesse_tableau_vf, vitesse_texte_trou
 *   niveau 3 → echelle_qcm, echelle_texte, echelle_calcul_echelle_texte, echelle_relier,
 *              echelle_tableau_vf, echelle_texte_trou, consommation_texte
 *   niveau 4 → vitesse_qcm et vitesse_texte en régime « conversion » (durée donnée en heures
 *              et minutes, réponse dans une autre unité), echelle_texte en régime « kilomètres »
 *
 * Points non tranchés (voir audit/5eme/984.md) :
 *   - Le rendement d'une recette, troisième contexte ajouté par la spec, n'est pas servi : sa
 *     réponse (un nombre de crêpes, de parts) n'appartient pas à la liste fermée
 *     `unite_de_la_reponse`.
 *   - Le tableau de proportionnalité « affiché en correction » (objets_geometriques 2, point de
 *     vigilance 8) n'a pas de canal de correction dans le payload ; il est servi comme SUPPORT
 *     de la carte à trous, grandeurs et unités nommées.
 *
 * Invariants : arithmétique entière (centimes, dixièmes de kilomètre, centimètres réels),
 * résultat à deux décimales au plus vérifié à la génération et non arrondi après coup, boucles
 * bornées avec repli déterministe (jamais de throw), distracteurs distincts de la bonne réponse
 * sous peine de nouveau tirage complet.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 984;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Une échelle écrite en fraction verticale là où l'élève la lit. */
const fxEch = (s) => String(s).replace(/(\d[\d ]*)\/(\d[\d ]*)/g,
    (m, a, b) => `$\\dfrac{${a.replace(/ /g, '')}}{${b.replace(/ /g, '')}}$`);

// ─── Mots-clés des erreurs visées (spec 984.json) ───
export const ERREURS_VISEES = {
    operation_inversee: 'Multiplier quand il faut diviser, et l\'inverse',
    coefficient_inferieur_a_un_divise: 'Croire qu\'un coefficient inférieur à 1 ne se multiplie pas, et chercher une division là où la multiplication s\'impose',
    unites_melangees: 'Mélanger deux unités dans le même calcul (des minutes et des heures, des centimètres et des kilomètres)',
    duree_en_h_min_au_lieu_de_minutes: 'Rendre une durée en heures et minutes quand la question demande des minutes, ou l\'inverse',
    heure_decimale_confondue: 'Confondre 1 h 25 min et 1,25 h',
    nombre_sans_unite: 'Rendre un nombre sans unité, ou dans l\'unité de l\'énoncé plutôt que dans celle demandée',
};

export const REGLES_DISTRACTEURS_QCM = {
    ...ERREURS_VISEES,
    virgule_decalee: 'Résultat dix ou cent fois trop grand ou trop petit (complément)',
    coefficient_pris_pour_le_resultat: 'Rendre le coefficient au lieu du résultat (complément)',
};

// ═══════════════════════════════════════════════════════════
// HELPERS NUMÉRIQUES (arithmétique entière, écriture française)
// ═══════════════════════════════════════════════════════════

/** Entier n divisé par 10^k → écriture décimale exacte, sans zéro inutile. */
const divPuissance10 = (n, k) => {
    if (k === 0) return String(n);
    const s = String(n).padStart(k + 1, '0');
    const ent = s.slice(0, s.length - k);
    const dec = s.slice(s.length - k).replace(/0+$/, '');
    return dec ? `${ent},${dec}` : ent;
};

/** Centimes → montant français sans zéro inutile (« 1250 » → « 12,5 »). */
const centimes = (c) => divPuissance10(c, 2);
/** Centimes → montant à deux décimales imposées (« 1250 » → « 12,50 »). */
const centimes2 = (c) => `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;
/** Dixièmes → écriture française (« 125 » → « 12,5 »). */
const dixiemes = (d) => divPuissance10(d, 1);

/** Le nombre de décimales d'un entier divisé par 10^k, après simplification. */
const nbDecimales = (n, k) => {
    const s = divPuissance10(n, k);
    const i = s.indexOf(',');
    return i < 0 ? 0 : s.length - i - 1;
};

// ─── Écritures admises d'une grandeur (liste finie, comparaison = 'expression') ───

const NOMS_UNITES = {
    '€': ['€', 'euro', 'euros'],
    km: ['km', 'kilomètre', 'kilomètres', 'kilometre', 'kilometres'],
    m: ['m', 'mètre', 'mètres', 'metre', 'metres'],
    cm: ['cm', 'centimètre', 'centimètres', 'centimetre', 'centimetres'],
    L: ['L', 'l', 'litre', 'litres'],
    min: ['min', 'minute', 'minutes'],
    h: ['h', 'heure', 'heures'],
};

/** Toutes les écritures « nombre unité » d'une liste de couples [texteNombre, unite]. */
const ecrituresGrandeur = (couples) => {
    const out = [];
    for (const [nombre, unite] of couples) {
        for (const u of NOMS_UNITES[unite]) {
            for (const e of [`${nombre} ${u}`, `${nombre}${u}`]) {
                if (!out.includes(e)) out.push(e);
            }
        }
    }
    return out;
};

/** Écritures admises d'un montant en centimes (« 12,50 € », « 12,5 € », « 12,50 euros »…). */
const ecrituresMontant = (c) => {
    const nombres = new Set([centimes(c), centimes2(c)]);
    return ecrituresGrandeur([...nombres].map((n) => [n, '€']));
};

/** Écritures admises d'un nombre décimal exact avec une unité simple. */
const ecrituresValeur = (texteNombre, unite) => {
    const nombres = new Set([texteNombre]);
    if (texteNombre.includes(',')) nombres.add(`${texteNombre}0`);
    else nombres.add(`${texteNombre},0`);
    return ecrituresGrandeur([...nombres].map((n) => [n, unite]));
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 1 — PRIX UNITAIRE ET CHANGE (niveaux 1 et 2)
// ═══════════════════════════════════════════════════════════

/** Articles vendus à l'unité ou au kilogramme, avec leur plage de prix en centimes. */
const ARTICLES = [
    { nom: 'cahiers', unite: 'cahier', quantite: (n) => `${n} cahiers`, parUnite: "l'unité", prix: [80, 120, 150, 180, 250, 320] },
    { nom: 'croissants', unite: 'croissant', quantite: (n) => `${n} croissants`, parUnite: "l'unité", prix: [90, 110, 120, 140] },
    { nom: 'places de cinéma', unite: 'place', quantite: (n) => `${n} places de cinéma`, parUnite: "l'unité", prix: [450, 550, 620, 750] },
    { nom: 'plants de tomates', unite: 'plant', quantite: (n) => `${n} plants de tomates`, parUnite: "l'unité", prix: [40, 60, 90, 130] },
    { nom: 'pommes', unite: 'kilogramme de pommes', quantite: (n) => `${n} kg de pommes`, parUnite: 'le kilogramme', prix: [120, 180, 240, 320] },
    { nom: 'cerises', unite: 'kilogramme de cerises', quantite: (n) => `${n} kg de cerises`, parUnite: 'le kilogramme', prix: [450, 600, 750] },
    { nom: 'ballons de handball', unite: 'ballon', quantite: (n) => `${n} ballons de handball`, parUnite: "l'unité", prix: [1200, 1800, 2400, 3500] },
    { nom: 'maillots du club', unite: 'maillot', quantite: (n) => `${n} maillots du club`, parUnite: "l'unité", prix: [1500, 2200, 2800, 4500] },
    { nom: 'billets de concert', unite: 'billet', quantite: (n) => `${n} billets de concert`, parUnite: "l'unité", prix: [2400, 3000, 4200, 6000] },
];

/** Monnaies du contexte « change monétaire » : combien d'euros vaut une unité. */
const MONNAIES = [
    { nom: 'francs suisses', unite: 'franc suisse', quantite: (n) => `${n} francs suisses`, taux: [105, 108, 112] },
    { nom: 'dollars canadiens', unite: 'dollar canadien', quantite: (n) => `${n} dollars canadiens`, taux: [65, 68, 72] },
    { nom: 'livres sterling', unite: 'livre sterling', quantite: (n) => `${n} livres sterling`, taux: [115, 118, 125] },
];

/**
 * Situation de prix : q unités à `pu` centimes l'unité, total exact au centime.
 * `sens` vaut « appliquer » (on demande le total) ou « remonter » (on demande le
 * prix d'une unité à partir du total).
 */
const tirerPrix = (sens) => {
    for (let essai = 0; essai < 60; essai++) {
        const change = randInt(0, 3) === 0;
        const source = change ? randomChoice(MONNAIES) : randomChoice(ARTICLES);
        const pu = change ? randomChoice(source.taux) : randomChoice(source.prix);
        const q = randInt(2, 24);
        const total = pu * q;
        if (total > 50000) continue;                       // au-delà, le décor n'est plus crédible
        if (sens === 'remonter' && total % q !== 0) continue;
        return { change, source, pu, q, total, sens };
    }
    return { change: false, source: ARTICLES[0], pu: 150, q: 6, total: 900, sens };
};

/**
 * Texte complet d'une situation de prix. Au sens « remonter », le prix d'une unité
 * n'est JAMAIS donné par l'énoncé : ce serait la réponse.
 */
const textePrix = (s, avecConsigne) => {
    const parUnite = s.source.parUnite;
    const consigne = avecConsigne
        ? " Écris la réponse avec son unité, par exemple : 12,50 €."
        : '';
    if (s.sens === 'appliquer') {
        return s.change
            ? `<p>Un bureau de change affiche 1 ${s.source.unite} pour ${centimes2(s.pu)} €.</p>`
                + `<p>Quelle est la valeur de ${s.source.quantite(s.q)} en euros ?${consigne}</p>`
            : `<p>Un magasin vend des ${s.source.nom} au prix de ${centimes2(s.pu)} € ${parUnite}.</p>`
                + `<p>Combien coûtent ${s.source.quantite(s.q)} ?${consigne}</p>`;
    }
    return s.change
        ? `<p>Dans un bureau de change, ${s.source.quantite(s.q)} valent ${centimes2(s.total)} €.</p>`
            + `<p>Quelle est la valeur de 1 ${s.source.unite} en euros ?${consigne}</p>`
        : `<p>Dans un magasin, ${s.source.quantite(s.q)} coûtent ${centimes2(s.total)} €.</p>`
            + `<p>Combien coûte 1 ${s.source.unite} ?${consigne}</p>`;
};

const genPrixQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const sens = randInt(0, 1) === 0 ? 'appliquer' : 'remonter';
        const s = tirerPrix(sens);
        const bonC = sens === 'appliquer' ? s.total : s.pu;
        const bonne = `${centimes2(bonC)} €`;
        const distracteurs = [];
        if (sens === 'appliquer') {
            distracteurs.push({ code: 'operation_inversee', valeur: `${centimes2(Math.round(s.pu / s.q))} €` });
            distracteurs.push({ code: 'coefficient_pris_pour_le_resultat', valeur: `${centimes2(s.pu)} €` });
            distracteurs.push({ code: 'virgule_decalee', valeur: `${centimes2(s.total * 10)} €` });
        } else {
            distracteurs.push({ code: 'operation_inversee', valeur: `${centimes2(s.total * s.q)} €` });
            distracteurs.push({ code: 'coefficient_pris_pour_le_resultat', valeur: `${centimes2(s.total)} €` });
            distracteurs.push({ code: 'virgule_decalee', valeur: `${centimes2(s.pu * 10)} €` });
        }
        const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const texte = textePrix(s, false);
        const payload = makeQcm(valeurs, bonne, texte,
            opts(sens === 'appliquer' ? 'Calculer un prix total' : 'Calculer un prix unitaire'));
        payload._meta.params = {
            niveau: sens === 'appliquer' ? 1 : 2, contexte: s.change ? 'change' : 'prix_unitaire',
            sens, coefficient: s.pu / 100, natureCoefficient: s.pu % 100 === 0 ? 'entier' : 'decimal_sup_1',
            depart: s.q, reponse: bonne, distracteurs,
        };
        return payload;
    }
    const bonne = '9,00 €';
    const distracteurs = [
        { code: 'operation_inversee', valeur: '0,25 €' },
        { code: 'coefficient_pris_pour_le_resultat', valeur: '1,50 €' },
        { code: 'virgule_decalee', valeur: '90,00 €' },
    ];
    const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        '<p>Un magasin vend des cahiers au prix de 1,50 € l\'unité.</p><p>Combien coûtent 6 cahiers ?</p>',
        opts('Calculer un prix total'));
    payload._meta.params = {
        niveau: 1, contexte: 'prix_unitaire', sens: 'appliquer', coefficient: 1.5,
        natureCoefficient: 'decimal_sup_1', depart: 6, reponse: bonne, distracteurs,
    };
    return payload;
};

const genPrixTexte = () => {
    const sens = randInt(0, 1) === 0 ? 'appliquer' : 'remonter';
    const s = tirerPrix(sens);
    const bonC = sens === 'appliquer' ? s.total : s.pu;
    const payload = makeSaisie(
        `${centimes2(bonC)} €`,
        textePrix(s, true),
        {
            ...opts(sens === 'appliquer' ? 'Calculer un prix total' : 'Calculer un prix unitaire'),
            comparaison: 'expression', reponses_acceptees: ecrituresMontant(bonC),
        },
    );
    payload._meta.params = {
        niveau: sens === 'appliquer' ? 1 : 2, contexte: s.change ? 'change' : 'prix_unitaire',
        sens, coefficient: s.pu / 100, natureCoefficient: s.pu % 100 === 0 ? 'entier' : 'decimal_sup_1',
        depart: s.q, reponse: `${centimes2(bonC)} €`, erreurVisee: 'nombre_sans_unite',
    };
    return payload;
};

const genPrixRelier = () => {
    const article = randomChoice(ARTICLES);
    const pu = randomChoice(article.prix);
    const quantites = shuffle([2, 3, 4, 5, 6, 8, 10, 12]).slice(0, 4).sort((a, b) => a - b);
    const pairs = quantites.map((q) => ({
        source: article.quantite(q),
        target: `${centimes2(pu * q)} €`,
    }));
    const payload = makeRelier(
        shuffle(pairs),
        `<p>Un magasin vend des ${article.nom} au prix de ${centimes2(pu)} € ${article.parUnite}.</p>`
        + '<p>Relie chaque quantité au prix qui lui correspond.</p>',
        opts('Relier quantités et prix'),
    );
    payload._meta.params = {
        niveau: 1, contexte: 'prix_unitaire', sens: 'appliquer', coefficient: pu / 100,
        natureCoefficient: pu % 100 === 0 ? 'entier' : 'decimal_sup_1', depart: quantites[0],
        erreursVisees: ['operation_inversee'],
    };
    return payload;
};

const genPrixVF = () => {
    const article = randomChoice(ARTICLES);
    const pu = randomChoice(article.prix);
    const plan = shuffle([true, true, false, false]);
    const lignes = [];
    const bonnes = [];
    const distracteurs = [];
    const quantites = shuffle([2, 3, 4, 5, 6, 8, 10]).slice(0, 4);
    plan.forEach((vrai, i) => {
        const q = quantites[i];
        const juste = pu * q;
        let valeur = juste;
        let code = null;
        if (!vrai) {
            const candidat = i % 2 === 0 ? juste * 10 : pu + q * 100;
            valeur = candidat === juste ? juste * 10 : candidat;
            code = valeur === juste * 10 ? 'virgule_decalee' : 'operation_inversee';
        }
        lignes.push(`${article.quantite(q)} coûtent ${centimes2(valeur)} €`);
        bonnes.push(vrai ? 0 : 1);
        if (code) distracteurs.push({ code, valeur: centimes2(valeur) });
    });
    const payload = makeVraiFaux(
        lignes, bonnes,
        `<p>Un magasin vend des ${article.nom} au prix de ${centimes2(pu)} € ${article.parUnite}.</p>`
        + '<p>Pour chaque ligne, indique si le prix annoncé est vrai ou faux.</p>',
        opts('Vrai/Faux sur prix unitaires'),
    );
    payload._meta.params = {
        niveau: 2, contexte: 'prix_unitaire', sens: 'appliquer', coefficient: pu / 100,
        natureCoefficient: pu % 100 === 0 ? 'entier' : 'decimal_sup_1', depart: quantites[0],
        distracteurs,
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 2 — VITESSE MOYENNE (niveaux 1, 2 et 4)
// ═══════════════════════════════════════════════════════════

/** Mobiles vraisemblables : allure et durées plausibles (point de vigilance 5). */
const MOBILES = [
    { nom: 'Un marcheur', sujet: 'le marcheur', Sujet: 'Le marcheur', verbe: 'marche', vitesses: [4, 5, 6], durees: [15, 24, 30, 45, 75, 90] },
    { nom: 'Une cycliste', sujet: 'la cycliste', Sujet: 'La cycliste', verbe: 'roule', vitesses: [12, 15, 18, 20, 24], durees: [20, 30, 40, 45, 75, 90, 120] },
    { nom: 'Un bus', sujet: 'le bus', Sujet: 'Le bus', verbe: 'roule', vitesses: [30, 36, 40, 45], durees: [12, 20, 25, 30, 45, 50] },
    { nom: 'Une voiture', sujet: 'la voiture', Sujet: 'La voiture', verbe: 'roule', vitesses: [70, 80, 90, 100, 110], durees: [12, 18, 24, 30, 45, 90, 120] },
    { nom: 'Un train', sujet: 'le train', Sujet: 'Le train', verbe: 'roule', vitesses: [90, 110, 120, 130], durees: [30, 45, 90, 120, 150, 180] },
];

/**
 * Trajet à allure constante : vitesse entière, durée entière en minutes, distance
 * exacte à un dixième de kilomètre près (v × t multiple de 6). Aux niveaux 1 et 2,
 * ni la vitesse 60 ni la durée 60 (exclusion 5).
 */
const tirerTrajet = (niveau) => {
    for (let essai = 0; essai < 80; essai++) {
        const m = randomChoice(MOBILES);
        const v = randomChoice(m.vitesses);
        const t = randomChoice(m.durees);
        if (niveau <= 2 && (v === 60 || t === 60)) continue;
        if ((v * t) % 6 !== 0) continue;
        const distanceDixiemes = (v * t) / 6;
        // La distance sert aussi de grandeur de départ (sens « remonter ») :
        // elle reste dans [0,5 ; 500] (variable valeur_de_la_grandeur_de_depart).
        if (distanceDixiemes < 5 || distanceDixiemes > 5000) continue;
        return { m, v, t, distanceDixiemes };
    }
    return { m: MOBILES[1], v: 18, t: 40, distanceDixiemes: 120 };
};

/** Une durée en minutes écrite en heures et minutes (« 90 » → « 1 h 30 min »). */
const enHeuresMinutes = (t) => {
    const h = Math.floor(t / 60);
    const min = t % 60;
    if (h === 0) return `${min} min`;
    return min === 0 ? `${h} h` : `${h} h ${min} min`;
};

const genVitesseQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Les deux sens à parité (point de vigilance 6) ; la conversion du niveau 4
        // se tire ensuite, à l'intérieur du sens « appliquer ».
        const sens = randInt(0, 1) === 0 ? 'appliquer' : 'remonter';
        const conversion = sens === 'appliquer' && randInt(0, 2) === 0;
        const niveau = conversion ? 4 : (sens === 'appliquer' ? 1 : 2);
        const tr = tirerTrajet(niveau);
        if (conversion && tr.t < 60) continue;
        const bonne = sens === 'appliquer'
            ? `${dixiemes(tr.distanceDixiemes)} km`
            : String(tr.v);
        const distracteurs = [];
        if (sens === 'appliquer') {
            distracteurs.push({ code: 'unites_melangees', valeur: `${tr.v * tr.t} km` });
            distracteurs.push({ code: 'operation_inversee', valeur: `${dixiemes(Math.round((tr.t * 600) / tr.v))} km` });
            distracteurs.push({ code: 'nombre_sans_unite', valeur: dixiemes(tr.distanceDixiemes) });
        } else {
            distracteurs.push({ code: 'operation_inversee', valeur: String(Math.round((tr.distanceDixiemes * tr.t) / 10)) });
            distracteurs.push({ code: 'unites_melangees', valeur: dixiemes(Math.round((tr.distanceDixiemes * 10) / tr.t)) });
            distracteurs.push({ code: 'coefficient_pris_pour_le_resultat', valeur: dixiemes(tr.distanceDixiemes) });
        }
        const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const dureeTexte = conversion ? enHeuresMinutes(tr.t) : `${tr.t} min`;
        const texte = sens === 'appliquer'
            ? `<p>${tr.m.nom} ${tr.m.verbe} à l'allure constante de ${tr.v} km par heure pendant ${dureeTexte}.</p><p>Quelle distance parcourt ${tr.m.sujet} ?</p>`
            : `<p>${tr.m.nom} parcourt ${dixiemes(tr.distanceDixiemes)} km en ${dureeTexte} à allure constante.</p><p>Quelle est sa vitesse moyenne, en kilomètres par heure ?</p>`;
        const payload = makeQcm(valeurs, bonne, texte,
            opts(sens === 'appliquer' ? 'Calculer une distance (vitesse × temps)' : 'Calculer une vitesse moyenne'));
        payload._meta.params = {
            niveau, contexte: 'vitesse', sens, coefficient: tr.v, natureCoefficient: 'entier',
            vitesse: tr.v, duree: tr.t, depart: tr.t, conversion,
            distance: dixiemes(tr.distanceDixiemes), reponse: bonne, distracteurs,
        };
        return payload;
    }
    const bonne = '12 km';
    const distracteurs = [
        { code: 'unites_melangees', valeur: '720 km' },
        { code: 'operation_inversee', valeur: '2,2 km' },
        { code: 'nombre_sans_unite', valeur: '12' },
    ];
    const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        '<p>Une cycliste roule à l\'allure constante de 18 km par heure pendant 40 min.</p><p>Quelle distance parcourt la cycliste ?</p>',
        opts('Calculer une distance (vitesse × temps)'));
    payload._meta.params = {
        niveau: 1, contexte: 'vitesse', sens: 'appliquer', coefficient: 18, natureCoefficient: 'entier',
        vitesse: 18, duree: 40, depart: 40, conversion: false, distance: '12', reponse: bonne, distracteurs,
    };
    return payload;
};

const genVitesseTexte = () => {
    // Les deux sens à parité (point de vigilance 6) : une fois sur deux on applique le
    // coefficient (la distance), une fois sur deux on remonte (la durée ou la vitesse).
    const regime = randInt(0, 1) === 0 ? 0 : (randInt(0, 1) === 0 ? 1 : 2);
    const conversion = regime === 0 && randInt(0, 1) === 0;
    const niveau = conversion ? 4 : (regime === 2 ? 2 : 1);
    const tr = tirerTrajet(niveau);
    if (regime === 1) {
        // Remonter à la durée : la réponse est une durée en minutes (erreur visée 4).
        const payload = makeSaisie(
            `${tr.t} min`,
            `<p>${tr.m.nom} parcourt ${dixiemes(tr.distanceDixiemes)} km à l'allure constante de ${tr.v} km par heure.</p>`
            + '<p>Combien de temps dure le trajet ? Écris la réponse en minutes, avec son unité, par exemple : 45 min.</p>',
            {
                ...opts('Calculer un temps (distance ÷ vitesse)'),
                comparaison: 'expression', reponses_acceptees: ecrituresValeur(String(tr.t), 'min'),
            },
        );
        payload._meta.params = {
            niveau: 2, contexte: 'vitesse', sens: 'remonter', coefficient: tr.v, natureCoefficient: 'entier',
            vitesse: tr.v, duree: tr.t, depart: tr.distanceDixiemes / 10, conversion: false,
            reponse: `${tr.t} min`, erreurVisee: 'duree_en_h_min_au_lieu_de_minutes',
        };
        return payload;
    }
    if (regime === 2) {
        // Le coefficient lui-même : la vitesse moyenne, sans unité dans la réponse.
        const payload = makeSaisie(
            String(tr.v),
            `<p>${tr.m.nom} parcourt ${dixiemes(tr.distanceDixiemes)} km en ${tr.t} min à allure constante.</p>`
            + '<p>Quelle est sa vitesse moyenne ? Écris seulement le nombre, l\'unité est déjà affichée.</p>',
            { ...opts('Calculer une vitesse moyenne'), suffixe: 'km/h' },
        );
        payload._meta.params = {
            niveau: 2, contexte: 'vitesse', sens: 'remonter', coefficient: tr.v, natureCoefficient: 'entier',
            vitesse: tr.v, duree: tr.t, depart: tr.distanceDixiemes / 10, conversion: false,
            reponse: String(tr.v), erreurVisee: 'unites_melangees',
        };
        return payload;
    }
    const dureeTexte = conversion && tr.t >= 60 ? enHeuresMinutes(tr.t) : `${tr.t} min`;
    const payload = makeSaisie(
        `${dixiemes(tr.distanceDixiemes)} km`,
        `<p>${tr.m.nom} ${tr.m.verbe} à l'allure constante de ${tr.v} km par heure pendant ${dureeTexte}.</p>`
        + `<p>Quelle distance parcourt ${tr.m.sujet} ? Écris la réponse en kilomètres, avec son unité, par exemple : 12,5 km.</p>`,
        {
            ...opts('Calculer une distance (vitesse × temps)'),
            comparaison: 'expression', reponses_acceptees: ecrituresValeur(dixiemes(tr.distanceDixiemes), 'km'),
        },
    );
    payload._meta.params = {
        niveau: conversion && tr.t >= 60 ? 4 : 1, contexte: 'vitesse', sens: 'appliquer',
        coefficient: tr.v, natureCoefficient: 'entier', vitesse: tr.v, duree: tr.t, depart: tr.t,
        conversion: conversion && tr.t >= 60, reponse: `${dixiemes(tr.distanceDixiemes)} km`,
        erreurVisee: conversion && tr.t >= 60 ? 'heure_decimale_confondue' : 'nombre_sans_unite',
    };
    return payload;
};

const genVitesseVF = () => {
    let tr = tirerTrajet(2);
    // La quatrième ligne double la durée : elle doit rester dans [6, 240] minutes.
    for (let essai = 0; essai < 40 && (tr.t * 2 > 240 || tr.t * 2 === 60); essai++) tr = tirerTrajet(2);
    if (tr.t * 2 > 240 || tr.t * 2 === 60) tr = { m: MOBILES[1], v: 18, t: 40, distanceDixiemes: 120 };
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];
    const lignes = [
        plan[0]
            ? `En ${tr.t} min, ${tr.m.sujet} parcourt ${dixiemes(tr.distanceDixiemes)} km`
            : `En ${tr.t} min, ${tr.m.sujet} parcourt ${tr.v * tr.t} km`,
        plan[1]
            ? `En 2 h, ${tr.m.sujet} parcourt ${2 * tr.v} km`
            : `En 2 h, ${tr.m.sujet} parcourt ${tr.v} km`,
        plan[2]
            ? `${tr.m.Sujet} met ${tr.t * 2} min pour parcourir ${dixiemes(tr.distanceDixiemes * 2)} km`
            : `${tr.m.Sujet} met ${tr.t * 2} min pour parcourir ${dixiemes(tr.distanceDixiemes)} km`,
        plan[3]
            ? `En 3 h, ${tr.m.sujet} parcourt ${3 * tr.v} km`
            : `En 3 h, ${tr.m.sujet} parcourt ${tr.v * 2} km`,
    ];
    const bonnes = [
        plan[0] ? 0 : 1,
        plan[1] ? 0 : 1,
        plan[2] ? 0 : 1,
        plan[3] ? 0 : 1,
    ];
    if (!plan[0]) distracteurs.push({ code: 'unites_melangees', valeur: String(tr.v * tr.t) });
    if (!plan[1]) distracteurs.push({ code: 'operation_inversee', valeur: String(tr.v) });
    if (!plan[2]) distracteurs.push({ code: 'operation_inversee', valeur: dixiemes(tr.distanceDixiemes) });
    if (!plan[3]) distracteurs.push({ code: 'virgule_decalee', valeur: String(tr.v * 2) });
    const payload = makeVraiFaux(
        lignes, bonnes,
        `<p>${tr.m.nom} ${tr.m.verbe} à l'allure constante de ${tr.v} km par heure.</p>`
        + '<p>Pour chaque ligne, indique si l\'affirmation est vraie ou fausse.</p>',
        opts('Vrai/Faux sur calculs de vitesse'),
    );
    payload._meta.params = {
        niveau: 2, contexte: 'vitesse', sens: 'appliquer', coefficient: tr.v, natureCoefficient: 'entier',
        vitesse: tr.v, duree: tr.t, depart: tr.t, conversion: false, distracteurs,
    };
    return payload;
};

const genVitesseTexteTrou = () => {
    for (let essai = 0; essai < 60; essai++) {
        const tr = tirerTrajet(2);
        const t2 = tr.t * 2;
        const t3 = Math.round(tr.t / 2);
        if (t3 < 6 || t3 * tr.v % 6 !== 0 || t2 > 240) continue;
        if (t2 === 60 || t3 === 60) continue;                 // exclusion 5 : jamais 60 minutes
        const d1 = tr.distanceDixiemes;
        const d2 = (tr.v * t2) / 6;
        const d3 = (tr.v * t3) / 6;
        const valeurs = [d1, d2, d3];
        if (new Set(valeurs.map(dixiemes)).size !== 3) continue;
        const phrase = `Durée du trajet en minutes : ${tr.t} puis ${t2} puis ${t3}\n`
            + `Distance parcourue en kilomètres : {{${dixiemes(d1)}}} puis {{${dixiemes(d2)}}} puis {{${dixiemes(d3)}}}`;
        const distracteurs = [
            { code: 'unites_melangees', valeur: String(tr.v * tr.t) },
            { code: 'operation_inversee', valeur: dixiemes(Math.round((tr.t * 600) / tr.v)) },
            { code: 'coefficient_pris_pour_le_resultat', valeur: String(tr.v) },
        ];
        const textesD = new Set(valeurs.map(dixiemes));
        if (distracteurs.some((d) => textesD.has(d.valeur))) continue;
        if (new Set(distracteurs.map((d) => d.valeur)).size !== 3) continue;
        const payload = makeTexteTrou(
            phrase,
            `<p>${tr.m.nom} ${tr.m.verbe} à l'allure constante de ${tr.v} km par heure.</p>`
            + '<p>Complète le tableau de la durée et de la distance.</p>',
            { ...opts('Compléter tableau vitesse'), mode: 'drag', distracteurs: distracteurs.map((d) => d.valeur) },
        );
        payload._meta.params = {
            niveau: 2, contexte: 'vitesse', sens: 'appliquer', coefficient: tr.v, natureCoefficient: 'entier',
            vitesse: tr.v, duree: tr.t, depart: tr.t, conversion: false, distracteurs,
        };
        return payload;
    }
    const phrase = 'Durée du trajet en minutes : 40 puis 80 puis 20\n'
        + 'Distance parcourue en kilomètres : {{12}} puis {{24}} puis {{6}}';
    const distracteurs = [
        { code: 'unites_melangees', valeur: '720' },
        { code: 'operation_inversee', valeur: '133,3' },
        { code: 'coefficient_pris_pour_le_resultat', valeur: '18' },
    ];
    const payload = makeTexteTrou(
        phrase,
        '<p>Une cycliste roule à l\'allure constante de 18 km par heure.</p>'
        + '<p>Complète le tableau de la durée et de la distance.</p>',
        { ...opts('Compléter tableau vitesse'), mode: 'drag', distracteurs: distracteurs.map((d) => d.valeur) },
    );
    payload._meta.params = {
        niveau: 2, contexte: 'vitesse', sens: 'appliquer', coefficient: 18, natureCoefficient: 'entier',
        vitesse: 18, duree: 40, depart: 40, conversion: false, distracteurs,
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// CATÉGORIE 3 — ÉCHELLE ET CONSOMMATION (niveaux 3 et 4)
// ═══════════════════════════════════════════════════════════

const ECHELLES = [100, 200, 500, 1000, 2000, 5000, 25000, 50000, 100000, 500000];

const LIEUX_PLAN = [
    { nom: 'le plan d\'un appartement', objet: 'le salon', echelles: [100, 200, 500] },
    { nom: 'le plan d\'un collège', objet: 'le gymnase', echelles: [200, 500, 1000] },
    { nom: 'le plan d\'un parc', objet: 'l\'allée centrale', echelles: [500, 1000, 2000] },
    { nom: 'la carte d\'une région', objet: 'la route entre deux villages', echelles: [25000, 50000, 100000] },
    { nom: 'la carte d\'un pays', objet: 'la voie ferrée entre deux villes', echelles: [100000, 500000] },
];

/**
 * Situation d'échelle : longueur sur le plan en dixièmes de centimètre, longueur
 * réelle exacte, rendue en mètres ou en kilomètres avec deux décimales au plus.
 */
const tirerEchelle = () => {
    for (let essai = 0; essai < 80; essai++) {
        const lieu = randomChoice(LIEUX_PLAN);
        const k = randomChoice(lieu.echelles);
        const planDixiemes = randInt(15, 240);                // 1,5 cm à 24 cm
        const reelCm = (planDixiemes * k) / 10;               // exact : k multiple de 100
        if (!Number.isInteger(reelCm)) continue;
        const enKm = reelCm >= 100000;
        const unite = enKm ? 'km' : 'm';
        const puissance = enKm ? 5 : 2;
        if (nbDecimales(reelCm, puissance) > 2) continue;
        const reelTexte = divPuissance10(reelCm, puissance);
        // La longueur réelle sert de grandeur de départ au sens « remonter » :
        // elle reste dans [0,5 ; 500] de son unité d'affichage.
        if (parseFloat(reelTexte.replace(",", ".")) > 500) continue;
        return { lieu, k, planDixiemes, reelCm, unite, reelTexte };
    }
    return {
        lieu: LIEUX_PLAN[0], k: 100, planDixiemes: 45, reelCm: 450, unite: 'm', reelTexte: '4,5',
    };
};

const genEchelleQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const e = tirerEchelle();
        const bonne = `${e.reelTexte} ${e.unite}`;
        const distracteurs = [
            { code: 'unites_melangees', valeur: `${divPuissance10(e.reelCm, 0)} ${e.unite}` },
            { code: 'operation_inversee', valeur: `${divPuissance10(Math.round((e.planDixiemes * 1000) / e.k), 4)} ${e.unite}` },
            { code: 'nombre_sans_unite', valeur: e.reelTexte },
        ];
        const valeurs = [bonne, ...distracteurs.map((d) => d.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(
            valeurs, bonne,
            `<p>Sur ${e.lieu.nom}, à l'échelle ${fxEch(`1/${e.k}`)}, ${e.lieu.objet} mesure ${dixiemes(e.planDixiemes)} cm.</p>`
            + `<p>Quelle est sa longueur réelle, en ${e.unite === 'km' ? 'kilomètres' : 'mètres'} ?</p>`,
            opts('Calculer une dimension réelle à partir d\'une échelle'),
        );
        payload._meta.params = {
            niveau: 3, contexte: 'echelle', sens: 'appliquer', coefficient: 1 / e.k,
            natureCoefficient: 'fraction', echelle: e.k, depart: e.planDixiemes / 10,
            reponse: bonne, distracteurs,
        };
        return payload;
    }
    const bonne = '4,5 m';
    const distracteurs = [
        { code: 'unites_melangees', valeur: '450 m' },
        { code: 'operation_inversee', valeur: '0,045 m' },
        { code: 'nombre_sans_unite', valeur: '4,5' },
    ];
    const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        `<p>Sur le plan d'un appartement, à l'échelle ${fxEch('1/100')}, le salon mesure 4,5 cm.</p><p>Quelle est sa longueur réelle, en mètres ?</p>`,
        opts('Calculer une dimension réelle à partir d\'une échelle'));
    payload._meta.params = {
        niveau: 3, contexte: 'echelle', sens: 'appliquer', coefficient: 1 / 100,
        natureCoefficient: 'fraction', echelle: 100, depart: 4.5, reponse: bonne, distracteurs,
    };
    return payload;
};

const genEchelleTexte = () => {
    const sens = randInt(0, 1) === 0 ? 'appliquer' : 'remonter';
    const e = tirerEchelle();
    if (sens === 'remonter') {
        const planTexte = dixiemes(e.planDixiemes);
        const payload = makeSaisie(
            `${planTexte} cm`,
            `<p>Sur ${e.lieu.nom}, à l'échelle ${fxEch(`1/${e.k}`)}, ${e.lieu.objet} mesure ${e.reelTexte} ${e.unite} en réalité.</p>`
            + '<p>Quelle est sa longueur sur le plan ? Écris la réponse en centimètres, avec son unité, par exemple : 4,5 cm.</p>',
            {
                ...opts('Calculer une dimension sur le plan à partir d\'une échelle'),
                comparaison: 'expression', reponses_acceptees: ecrituresValeur(planTexte, 'cm'),
            },
        );
        payload._meta.params = {
            niveau: 3, contexte: 'echelle', sens: 'remonter', coefficient: 1 / e.k,
            natureCoefficient: 'fraction', echelle: e.k, depart: parseFloat(e.reelTexte.replace(',', '.')),
            reponse: `${planTexte} cm`, erreurVisee: 'operation_inversee',
        };
        return payload;
    }
    const payload = makeSaisie(
        `${e.reelTexte} ${e.unite}`,
        `<p>Sur ${e.lieu.nom}, à l'échelle ${fxEch(`1/${e.k}`)}, ${e.lieu.objet} mesure ${dixiemes(e.planDixiemes)} cm.</p>`
        + `<p>Quelle est sa longueur réelle ? Écris la réponse en ${e.unite === 'km' ? 'kilomètres' : 'mètres'}, avec son unité, par exemple : 4,5 ${e.unite}.</p>`,
        {
            ...opts('Calculer dimension réelle à partir d\'une échelle'),
            comparaison: 'expression', reponses_acceptees: ecrituresValeur(e.reelTexte, e.unite),
        },
    );
    payload._meta.params = {
        niveau: e.unite === 'km' ? 4 : 3, contexte: 'echelle', sens: 'appliquer',
        coefficient: 1 / e.k, natureCoefficient: 'fraction', echelle: e.k,
        depart: e.planDixiemes / 10, reponse: `${e.reelTexte} ${e.unite}`,
        erreurVisee: 'unites_melangees',
    };
    return payload;
};

const genEchelleCalculEchelle = () => {
    const e = tirerEchelle();
    const admises = [`1/${e.k}`, `1 / ${e.k}`, `1:${e.k}`, `1 : ${e.k}`];
    const payload = makeSaisie(
        `1/${e.k}`,
        `<p>Sur ${e.lieu.nom}, ${e.lieu.objet} mesure ${dixiemes(e.planDixiemes)} cm, et ${e.reelTexte} ${e.unite} en réalité.</p>`
        + '<p>Quelle est l\'échelle de ce document ? Écris-la sous la forme 1/n, par exemple : 1/500.</p>',
        { ...opts('Déterminer une échelle'), comparaison: 'expression', reponses_acceptees: admises },
    );
    payload._meta.params = {
        niveau: 3, contexte: 'echelle', sens: 'remonter', coefficient: 1 / e.k,
        natureCoefficient: 'fraction', echelle: e.k, depart: e.planDixiemes / 10,
        reponse: `1/${e.k}`, erreurVisee: 'operation_inversee',
    };
    return payload;
};

const genEchelleRelier = () => {
    const choisies = shuffle([...ECHELLES]).slice(0, 4).sort((a, b) => a - b);
    const pairs = choisies.map((k) => {
        const cm = k;
        const cible = cm >= 100000 ? `${divPuissance10(cm, 5)} km` : `${divPuissance10(cm, 2)} m`;
        return { source: `échelle 1/${k}`, target: `1 cm représente ${cible}` };
    });
    const payload = makeRelier(
        shuffle(pairs),
        '<p>Relie chaque échelle à ce que représente 1 cm du document dans la réalité.</p>',
        opts('Relier échelles et correspondances'),
    );
    payload._meta.params = {
        niveau: 3, contexte: 'echelle', sens: 'appliquer', coefficient: 1 / choisies[0],
        natureCoefficient: 'fraction', echelle: choisies[0], depart: 1,
        erreursVisees: ['unites_melangees'],
    };
    return payload;
};

const genEchelleVF = () => {
    const e = tirerEchelle();
    const plan = shuffle([true, true, false, false]);
    const distracteurs = [];
    const doubleTexte = divPuissance10(e.reelCm * 2, e.unite === 'km' ? 5 : 2);
    const lignes = [
        plan[0]
            ? `${dixiemes(e.planDixiemes)} cm sur le document représentent ${e.reelTexte} ${e.unite} en réalité`
            : `${dixiemes(e.planDixiemes)} cm sur le document représentent ${divPuissance10(e.reelCm, 0)} ${e.unite} en réalité`,
        plan[1]
            ? `${dixiemes(e.planDixiemes * 2)} cm sur le document représentent ${doubleTexte} ${e.unite} en réalité`
            : `${dixiemes(e.planDixiemes * 2)} cm sur le document représentent ${e.reelTexte} ${e.unite} en réalité`,
        plan[2]
            ? `1 cm sur le document représente ${e.k} cm en réalité`
            : `1 cm sur le document représente ${e.k} m en réalité`,
        plan[3]
            ? 'Sur ce document, une longueur réelle est plus grande que la longueur mesurée'
            : 'Sur ce document, une longueur réelle est plus petite que la longueur mesurée',
    ];
    if (!plan[0]) distracteurs.push({ code: 'unites_melangees', valeur: divPuissance10(e.reelCm, 0) });
    if (!plan[1]) distracteurs.push({ code: 'operation_inversee', valeur: e.reelTexte });
    if (!plan[2]) distracteurs.push({ code: 'unites_melangees', valeur: String(e.k) });
    if (!plan[3]) distracteurs.push({ code: 'coefficient_inferieur_a_un_divise', valeur: 'plus petite' });
    const payload = makeVraiFaux(
        lignes, plan.map((x) => (x ? 0 : 1)),
        `<p>${e.lieu.nom.charAt(0).toUpperCase()}${e.lieu.nom.slice(1)} est à l'échelle ${fxEch(`1/${e.k}`)}.</p>`
        + '<p>Pour chaque ligne, indique si l\'affirmation est vraie ou fausse.</p>',
        opts('Vrai/Faux sur les échelles'),
    );
    payload._meta.params = {
        niveau: 3, contexte: 'echelle', sens: 'appliquer', coefficient: 1 / e.k,
        natureCoefficient: 'fraction', echelle: e.k, depart: e.planDixiemes / 10, distracteurs,
    };
    return payload;
};

const genEchelleTexteTrou = () => {
    for (let essai = 0; essai < 60; essai++) {
        const e = tirerEchelle();
        const plans = [e.planDixiemes, e.planDixiemes * 2, Math.round(e.planDixiemes / 2)];
        if (plans[2] < 5) continue;
        const puissance = e.unite === 'km' ? 5 : 2;
        const reels = plans.map((p) => (p * e.k) / 10);
        if (reels.some((r) => !Number.isInteger(r) || nbDecimales(r, puissance) > 2)) continue;
        const textes = reels.map((r) => divPuissance10(r, puissance));
        if (new Set(textes).size !== 3) continue;
        const distracteurs = [
            { code: 'unites_melangees', valeur: divPuissance10(reels[0], 0) },
            { code: 'operation_inversee', valeur: divPuissance10(Math.round((plans[0] * 1000) / e.k), 4) },
            { code: 'virgule_decalee', valeur: divPuissance10(reels[0] * 10, puissance) },
        ];
        if (distracteurs.some((d) => textes.includes(d.valeur))) continue;
        if (new Set(distracteurs.map((d) => d.valeur)).size !== 3) continue;
        const phrase = `Longueur sur le document en centimètres : ${plans.map(dixiemes).join(' puis ')}\n`
            + `Longueur réelle en ${e.unite === 'km' ? 'kilomètres' : 'mètres'} : {{${textes[0]}}} puis {{${textes[1]}}} puis {{${textes[2]}}}`;
        const payload = makeTexteTrou(
            phrase,
            `<p>${e.lieu.nom.charAt(0).toUpperCase()}${e.lieu.nom.slice(1)} est à l'échelle ${fxEch(`1/${e.k}`)}.</p>`
            + '<p>Complète le tableau des longueurs.</p>',
            { ...opts('Compléter tableau échelle'), mode: 'drag', distracteurs: distracteurs.map((d) => d.valeur) },
        );
        payload._meta.params = {
            niveau: 3, contexte: 'echelle', sens: 'appliquer', coefficient: 1 / e.k,
            natureCoefficient: 'fraction', echelle: e.k, depart: plans[0] / 10, distracteurs,
        };
        return payload;
    }
    const phrase = 'Longueur sur le document en centimètres : 4,5 puis 9 puis 2,5\n'
        + 'Longueur réelle en mètres : {{4,5}} puis {{9}} puis {{2,5}}';
    const distracteurs = [
        { code: 'unites_melangees', valeur: '450' },
        { code: 'operation_inversee', valeur: '0,045' },
        { code: 'virgule_decalee', valeur: '45' },
    ];
    const payload = makeTexteTrou(
        phrase,
        `<p>Le plan d'un appartement est à l'échelle ${fxEch('1/100')}.</p><p>Complète le tableau des longueurs.</p>`,
        { ...opts('Compléter tableau échelle'), mode: 'drag', distracteurs: distracteurs.map((d) => d.valeur) },
    );
    payload._meta.params = {
        niveau: 3, contexte: 'echelle', sens: 'appliquer', coefficient: 1 / 100,
        natureCoefficient: 'fraction', echelle: 100, depart: 4.5, distracteurs,
    };
    return payload;
};

/** Consommation à l'unité : un coefficient décimal inférieur à 1 (graduation niveau 3). */
const VEHICULES = [
    { nom: 'Une citadine', parCent: [50, 55, 60] },
    { nom: 'Un monospace', parCent: [70, 75, 80] },
    { nom: 'Un utilitaire', parCent: [90, 100, 120] },
];

const genConsommationTexte = () => {
    for (let essai = 0; essai < 60; essai++) {
        const v = randomChoice(VEHICULES);
        const parCent = randomChoice(v.parCent);             // dixièmes de litre aux 100 km
        const sens = randInt(0, 1) === 0 ? 'appliquer' : 'remonter';
        const distance = randInt(2, 50) * 10;                // 20 à 500 km
        const litresCentiemes = (parCent * distance) / 100 * 10;  // centièmes de litre
        if (!Number.isInteger(litresCentiemes)) continue;
        if (nbDecimales(litresCentiemes, 2) > 2) continue;
        if (sens === 'appliquer') {
            const texte = divPuissance10(litresCentiemes, 2);
            const payload = makeSaisie(
                `${texte} L`,
                `<p>${v.nom} consomme ${dixiemes(parCent)} litres de carburant pour 100 km, soit ${divPuissance10(parCent, 3)} litre par kilomètre.</p>`
                + `<p>Quelle quantité de carburant faut-il prévoir pour ${distance} km ? Écris la réponse en litres, avec son unité, par exemple : 12,5 L.</p>`,
                {
                    ...opts('Calculer une consommation à partir d\'un coefficient'),
                    comparaison: 'expression', reponses_acceptees: ecrituresValeur(texte, 'L'),
                },
            );
            payload._meta.params = {
                niveau: 3, contexte: 'consommation', sens, coefficient: parCent / 1000,
                natureCoefficient: 'decimal_inf_1', depart: distance, reponse: `${texte} L`,
                erreurVisee: 'coefficient_inferieur_a_un_divise',
            };
            return payload;
        }
        const texteLitres = divPuissance10(litresCentiemes, 2);
        const payload = makeSaisie(
            `${distance} km`,
            `<p>${v.nom} consomme ${divPuissance10(parCent, 3)} litre de carburant par kilomètre.</p>`
            + `<p>Après un trajet, le réservoir s'est vidé de ${texteLitres} litres. Quelle distance a été parcourue ? Écris la réponse en kilomètres, avec son unité, par exemple : 120 km.</p>`,
            {
                ...opts('Remonter à la distance à partir d\'une consommation'),
                comparaison: 'expression', reponses_acceptees: ecrituresValeur(String(distance), 'km'),
            },
        );
        payload._meta.params = {
            niveau: 3, contexte: 'consommation', sens, coefficient: parCent / 1000,
            natureCoefficient: 'decimal_inf_1', depart: litresCentiemes / 100, reponse: `${distance} km`,
            erreurVisee: 'operation_inversee',
        };
        return payload;
    }
    const payload = makeSaisie(
        '12 L',
        '<p>Une citadine consomme 6 litres de carburant pour 100 km, soit 0,06 litre par kilomètre.</p>'
        + '<p>Quelle quantité de carburant faut-il prévoir pour 200 km ? Écris la réponse en litres, avec son unité, par exemple : 12,5 L.</p>',
        {
            ...opts('Calculer une consommation à partir d\'un coefficient'),
            comparaison: 'expression', reponses_acceptees: ecrituresValeur('12', 'L'),
        },
    );
    payload._meta.params = {
        niveau: 3, contexte: 'consommation', sens: 'appliquer', coefficient: 0.06,
        natureCoefficient: 'decimal_inf_1', depart: 200, reponse: '12 L',
        erreurVisee: 'coefficient_inferieur_a_un_divise',
    };
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    prix_qcm: genPrixQcm,
    prix_texte: genPrixTexte,
    prix_relier: genPrixRelier,
    prix_tableau_vf: genPrixVF,

    vitesse_qcm: genVitesseQcm,
    vitesse_texte: genVitesseTexte,
    vitesse_tableau_vf: genVitesseVF,
    vitesse_texte_trou: genVitesseTexteTrou,

    echelle_qcm: genEchelleQcm,
    echelle_texte: genEchelleTexte,
    echelle_calcul_echelle_texte: genEchelleCalculEchelle,
    echelle_relier: genEchelleRelier,
    echelle_tableau_vf: genEchelleVF,
    echelle_texte_trou: genEchelleTexteTrou,
    consommation_texte: genConsommationTexte,
};

export const CATEGORIES = [
    {
        id: 'prix',
        label: 'Prix unitaire et change monétaire',
        formatsList: [
            { key: 'prix_qcm', label: 'QCM' },
            { key: 'prix_texte', label: 'Saisie' },
            { key: 'prix_relier', label: 'Relier' },
            { key: 'prix_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'vitesse',
        label: 'Vitesse moyenne',
        formatsList: [
            { key: 'vitesse_qcm', label: 'QCM' },
            { key: 'vitesse_texte', label: 'Saisie' },
            { key: 'vitesse_tableau_vf', label: 'Vrai/Faux' },
            { key: 'vitesse_texte_trou', label: 'Compléter tableau' },
        ],
    },
    {
        id: 'echelle',
        label: 'Échelles et consommation à l\'unité (coefficient inférieur à 1)',
        formatsList: [
            { key: 'echelle_qcm', label: 'QCM' },
            { key: 'echelle_texte', label: 'Saisie' },
            { key: 'echelle_calcul_echelle_texte', label: 'Calculer échelle' },
            { key: 'echelle_relier', label: 'Relier' },
            { key: 'echelle_tableau_vf', label: 'Vrai/Faux' },
            { key: 'echelle_texte_trou', label: 'Compléter tableau' },
            { key: 'consommation_texte', label: 'Consommation à l\'unité' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
